import React, { useEffect, useRef, useState } from 'react'
import {
  DEFAULT_POSITION, boxKey, boxesOf, changeType, hasFontSettings, hasOptions, isSection, optionChoices, optionLabel,
  positionable, prefillable, requireable
} from './fieldTypes'
import TypePicker, { TypeIcon } from './TypePicker'
import OptionsEditor from './OptionsEditor'
import TableEditor from './TableEditor'
import ConditionEditor from './ConditionEditor'
import { Group, NumberInput, Row, Segmented, TextArea, Toggle } from './ui'
import { interpolate, useT } from './translations'

const FONTS  = ['Helvetica', 'Times-Roman', 'Courier']
const COLORS = ['#000000', '#1F4E79', '#2E75B6', '#C00000', '#548235', '#7F7F7F']
const STYLE_KEYS = ['font', 'font_size', 'font_size_auto', 'bold', 'italic', 'fill_color']

const alignOptions = (t) => [
  ['left',   <i className="fas fa-align-left" />,   t.align_text_left],
  ['center', <i className="fas fa-align-center" />, t.align_text_center],
  ['right',  <i className="fas fa-align-right" />,  t.align_text_right]
]

const pickStyle = (position) => Object.fromEntries(STYLE_KEYS.map((key) => [key, position[key]]))

const StyleControls = ({ style, onChange, disabled }) => {
  const t = useT()
  return (
    <>
      <Row label={t.font}>
        <select className="fb-input" value={style.font || 'Helvetica'} disabled={disabled} onChange={(event) => onChange({ font: event.target.value })}>
          {FONTS.map((font) => <option key={font} value={font}>{font}</option>)}
        </select>
      </Row>
      <div className="fb-grid-2 is-aligned">
        <Row label={t.font_size}>
          <NumberInput value={style.font_size} min={4} max={72} disabled={disabled || style.font_size_auto} onChange={(value) => onChange({ font_size: value || 10 })} />
        </Row>
        <div className="fb-style-buttons">
          <button type="button" title={t.bold} className={`fb-tool${style.bold ? ' active' : ''}`} disabled={disabled} onClick={() => onChange({ bold: !style.bold })}><i className="fas fa-bold" /></button>
          <button type="button" title={t.italic} className={`fb-tool${style.italic ? ' active' : ''}`} disabled={disabled} onClick={() => onChange({ italic: !style.italic })}><i className="fas fa-italic" /></button>
        </div>
      </div>
      <Toggle checked={style.font_size_auto} disabled={disabled} label={t.font_size_auto} hint={t.font_size_auto_hint} onChange={(value) => onChange({ font_size_auto: value })} />
      <Row label={t.color}>
        <div className="fb-swatches">
          {COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className={`fb-swatch${(style.fill_color || '').toLowerCase() === color.toLowerCase() ? ' active' : ''}`}
              style={{ background: color }}
              disabled={disabled}
              onClick={() => onChange({ fill_color: color })}
            />
          ))}
          <label className="fb-swatch is-custom" title={t.custom_color}>
            <input type="color" value={style.fill_color || '#000000'} disabled={disabled} onChange={(event) => onChange({ fill_color: event.target.value })} />
            <i className="fas fa-eye-dropper" />
          </label>
        </div>
      </Row>
    </>
  )
}

const PositionTab = ({ field, fields, pages, readOnly, styleClipboard, activeBox, placing, actions }) => {
  const t = useT()
  const boxes   = boxesOf(field)
  const choices = optionChoices(field, t)
  const active  = boxes.find((box) => box.key === activeBox) || boxes[0]
  const isPlacing = (option) => placing && placing.fieldId === field.id && placing.option === option

  if (boxes.length === 0 && choices.length === 0) {
    return (
      <div className="fb-unplaced">
        <div className="fb-unplaced-art"><i className="fas fa-crosshairs" /></div>
        <p>{t.not_placed_hint}</p>
        {!readOnly &&
          <button type="button" className="fb-button is-primary" onClick={() => actions.placeField(field.id, 1, 40, 40)}>
            <i className="fas fa-map-pin" /> {t.place_default}
          </button>
        }
      </div>
    )
  }

  const setBox = (box, changes) => actions.updatePositions({ [box.key]: { ...box.position, ...changes } })
  const setSize = (key, value) => {
    if (value === null || !active) { return }
    const position = active.position
    if (key === 'x') { setBox(active, { x1: value, x2: value + (position.x2 - position.x1) }) }
    if (key === 'y') { setBox(active, { y1: value, y2: value + (position.y2 - position.y1) }) }
    if (key === 'w') { setBox(active, { x2: position.x1 + Math.max(1, value) }) }
    if (key === 'h') { setBox(active, { y2: position.y1 + Math.max(1, value) }) }
  }

  const style = active ? { ...DEFAULT_POSITION, ...active.position } : DEFAULT_POSITION
  const setStyle = (changes) => actions.updatePositions(Object.fromEntries(boxes.map((box) => [box.key, { ...box.position, ...changes }])))

  const placeAllOptions = () => {
    const missing = choices.filter(([value]) => !(field.option_positions || {})[value])
    const anchor  = boxes[boxes.length - 1]?.position
    const page    = anchor ? anchor.page_number : 1
    let y         = anchor ? anchor.y2 + 8 : 40
    const x       = anchor ? anchor.x1 : 40
    const changes = {}
    missing.forEach(([value]) => {
      changes[boxKey(field.id, value)] = { ...pickStyle(style), page_number: page, x1: x, y1: y, x2: x + 12, y2: y + 12 }
      y += 20
    })
    actions.updatePositions(changes)
  }

  const boxRow = (option, label, hint) => {
    const key = boxKey(field.id, option)
    const box = boxes.find((item) => item.key === key)
    return (
      <div key={key} className={`fb-box-row${active && active.key === key ? ' active' : ''}`}>
        <button type="button" className="fb-box-row-main" disabled={!box} onClick={() => actions.setActiveBox(key)}>
          <span className={`fb-placed${box ? ' is-placed' : ''}`}><i className={box ? 'fas fa-map-pin' : 'far fa-circle'} /></span>
          <span className="fb-box-row-text">
            {label}
            {hint && <small>{hint}</small>}
          </span>
          {box && <span className="fb-box-row-page">{interpolate(t.page_short, { page: box.position.page_number })}</span>}
        </button>
        {!readOnly && (box
          ? <button type="button" className="fb-icon-button is-small is-danger" title={t.remove_position} onClick={() => actions.removePositions([key])}><i className="fas fa-times" /></button>
          : <button type="button" className={`fb-button is-small${isPlacing(option) ? ' is-primary' : ''}`} onClick={() => actions.setPlacing(isPlacing(option) ? null : { fieldId: field.id, option })}>
              {isPlacing(option) ? t.placing : t.place}
            </button>)}
      </div>
    )
  }

  const others = fields.filter((other) => other.id !== field.id && boxesOf(other).length > 0)

  return (
    <>
      <Group title={t.boxes}>
        {boxRow(null, t.answer_box, choices.length ? t.answer_box_choice_hint : t.answer_box_hint)}
        {choices.length > 0 &&
          <>
            <div className="fb-subtitle">
              <span>{t.option_boxes}</span>
              {!readOnly && choices.some(([value]) => !(field.option_positions || {})[value]) &&
                <button type="button" className="fb-link" onClick={placeAllOptions}><i className="fas fa-th-list" /> {t.place_all_options}</button>
              }
            </div>
            <div className="fb-prop-hint">{t.option_boxes_hint}</div>
            {choices.map(([value, label]) => boxRow(value, label))}
          </>
        }
      </Group>

      {active &&
        <Group title={interpolate(t.placement_of, { name: active.option === null ? t.answer_box : optionLabel(field, active.option, t) })}>
          <Row label={t.page}>
            <select className="fb-input" value={active.position.page_number} disabled={readOnly} onChange={(event) => setBox(active, { page_number: parseInt(event.target.value, 10) })}>
              {pages.map((_, index) => <option key={index} value={index + 1}>{interpolate(t.page_n, { page: index + 1 })}</option>)}
            </select>
          </Row>
          <div className="fb-grid-4">
            <Row label="X"><NumberInput value={active.position.x1} disabled={readOnly} onChange={(value) => setSize('x', value)} /></Row>
            <Row label="Y"><NumberInput value={active.position.y1} disabled={readOnly} onChange={(value) => setSize('y', value)} /></Row>
            <Row label={t.width_short}><NumberInput value={active.position.x2 - active.position.x1} min={1} disabled={readOnly} onChange={(value) => setSize('w', value)} /></Row>
            <Row label={t.height_short}><NumberInput value={active.position.y2 - active.position.y1} min={1} disabled={readOnly} onChange={(value) => setSize('h', value)} /></Row>
          </div>
          <div className="fb-prop-hint">{t.points_hint}</div>
        </Group>
      }

      {active && (hasFontSettings(field) || boxes.some((box) => box.option !== null)) &&
        <Group
          title={t.text_style}
          actions={
            <span className="fb-group-actions">
              <button type="button" className="fb-link" title={t.copy_style} onClick={() => actions.setStyleClipboard(pickStyle(style))}>
                <i className="far fa-copy" />
              </button>
              {styleClipboard && !readOnly &&
                <button type="button" className="fb-link" title={t.paste_style} onClick={() => setStyle(styleClipboard)}>
                  <i className="fas fa-paste" />
                </button>
              }
            </span>
          }
        >
          <StyleControls style={style} disabled={readOnly} onChange={setStyle} />
          {!readOnly && others.length > 0 &&
            <button
              type="button"
              className="fb-button is-block"
              onClick={() => {
                if (!window.confirm(interpolate(t.apply_style_confirm, { count: others.length }))) { return }
                actions.updatePositions(Object.fromEntries(others.flatMap(boxesOf).map((box) => [box.key, { ...box.position, ...pickStyle(style) }])))
              }}
            >
              <i className="fas fa-magic" /> {t.apply_style_all}
            </button>
          }
        </Group>
      }

      {!readOnly && boxes.length > 0 &&
        <button type="button" className="fb-button is-danger is-block" onClick={() => actions.removePositions(boxes.map((box) => box.key))}>
          <i className="fas fa-eraser" /> {boxes.length > 1 ? t.remove_all_positions : t.remove_position}
        </button>
      }
    </>
  )
}

const FieldTab = ({ field, fields, blueprint, hasPdf, readOnly, meta, actions }) => {
  const t = useT()
  const [picker, setPicker] = useState(false)
  const set = (changes) => actions.updateField(field.id, changes)
  const sections = fields.filter(isSection)
  const section = sections.find((item) => item.id === field.section_id)

  const moveToSection = (sectionId) => {
    const target = sectionId ? parseInt(sectionId, 10) : null
    const siblings = fields.filter((item) => (item.section_id || null) === target && item.id !== field.id)
    actions.reorder([{ id: field.id, section_id: target, order: siblings.length + 1 }])
  }

  return (
    <>
      <Group>
        <Row label={t.label}>
          <TextArea value={field.name} placeholder={t.untitled} disabled={readOnly} onChange={(name) => set({ name })} />
        </Row>
        <Row label={t.type}>
          <div className="fb-picker-anchor">
            <button type="button" className="fb-type-button" disabled={readOnly} onClick={() => setPicker(true)}>
              <TypeIcon type={field.data_type} small />
              <span>{t.types[field.data_type]}</span>
              <i className="fas fa-chevron-down" />
            </button>
            {picker &&
              <TypePicker
                value={field.data_type}
                exclude={section?.duplicable ? ['table'] : []}
                onClose={() => setPicker(false)}
                onPick={(type) => {
                  setPicker(false)
                  const next = changeType(field, type)
                  if (type === 'table' && !field.table_columns) {
                    next.table_columns = { ...next.table_columns, columns: [{ ...next.table_columns.columns[0], name: `${t.column_default} 1` }] }
                  }
                  actions.updateField(field.id, next)
                }}
              />
            }
          </div>
        </Row>
        <Row label={t.section}>
          <select className="fb-input" value={field.section_id || ''} disabled={readOnly} onChange={(event) => moveToSection(event.target.value)}>
            <option value="">{t.no_section}</option>
            {sections.map((item) => (
              <option key={item.id} value={item.id} disabled={field.data_type === 'table' && item.duplicable}>{item.name || t.untitled}</option>
            ))}
          </select>
        </Row>
      </Group>

      <Group title={t.behaviour}>
        {requireable(field) &&
          <Toggle checked={field.is_mandatory} disabled={readOnly} label={t.required} onChange={(value) => set({ is_mandatory: value })} />
        }
        {blueprint.can_be_filled &&
          <Toggle checked={field.can_be_filled} disabled={readOnly} label={t.can_be_filled} hint={t.can_be_filled_hint} onChange={(value) => set({ can_be_filled: value })} />
        }
        <Toggle checked={field.can_be_copied} disabled={readOnly} label={t.can_be_copied} hint={t.can_be_copied_hint} onChange={(value) => set({ can_be_copied: value })} />
        {prefillable(field) &&
          <Row label={t.prefill_with}>
            <select className="fb-input" value={field.prefill_with || ''} disabled={readOnly} onChange={(event) => set({ prefill_with: event.target.value || null })}>
              {meta.prefillOptions.map(([label, value]) => <option key={value || 'none'} value={value || ''}>{label}</option>)}
            </select>
          </Row>
        }
        {!hasPdf && !['table', 'page_break'].includes(field.data_type) &&
          <Row label={t.text_align} inline>
            <Segmented value={field.text_align || 'left'} options={alignOptions(t)} disabled={readOnly} onChange={(text_align) => set({ text_align })} />
          </Row>
        }
      </Group>

      <ConditionEditor target={field} fields={fields} readOnly={readOnly} onChange={(visible_when) => set({ visible_when })} />

      {hasOptions(field) &&
        <Group title={t.options}>
          <OptionsEditor
            options={field.options}
            prefills={field.options_prefill}
            withPrefill={field.data_type === 'select_prefill'}
            disabled={readOnly}
            onChange={(options, options_prefill) => set({ options, options_prefill })}
          />
          {field.data_type === 'select' &&
            <div className="fb-toggles">
              <Toggle checked={field.multi_option} disabled={readOnly} label={t.multi_option} onChange={(value) => set({ multi_option: value })} />
              <Toggle checked={field.has_extra_option} disabled={readOnly} label={t.has_extra_option} onChange={(value) => set({ has_extra_option: value })} />
              <Toggle checked={field.hide_unselected} disabled={readOnly} label={t.hide_unselected} onChange={(value) => set({ hide_unselected: value })} />
            </div>
          }
        </Group>
      }

      {field.data_type === 'table' && field.table_columns &&
        <TableEditor
          config={field.table_columns}
          hasPdf={hasPdf}
          meta={meta}
          disabled={readOnly}
          onChange={(table_columns) => set({ table_columns })}
        />
      }
    </>
  )
}

const FieldInspector = ({ field, fields, blueprint, pages, readOnly, meta, styleClipboard, activeBox, placing, actions }) => {
  const t = useT()
  const hasPdf = pages.length > 0
  const canPlace = hasPdf && positionable(field)
  const [tab, setTab] = useState('field')

  useEffect(() => { if (!canPlace && tab === 'position') { setTab('field') } }, [canPlace, tab])

  return (
    <>
      <div className="fb-inspector-head">
        <TypeIcon type={field.data_type} />
        <div className="fb-inspector-title">
          <strong>{field.name || t.untitled}</strong>
          <span>{t.types[field.data_type]}</span>
        </div>
        {!readOnly &&
          <div className="fb-inspector-actions">
            <button type="button" className="fb-icon-button" title={`${t.duplicate} (Ctrl+D)`} onClick={() => actions.duplicateField(field.id)}><i className="far fa-copy" /></button>
            <button type="button" className="fb-icon-button is-danger" title={t.delete} onClick={() => actions.deleteField(field.id)}><i className="far fa-trash-alt" /></button>
          </div>
        }
      </div>

      {canPlace &&
        <div className="fb-tabs">
          <button type="button" className={tab === 'field' ? 'active' : ''} onClick={() => setTab('field')}>{t.tab_field}</button>
          <button type="button" className={tab === 'position' ? 'active' : ''} onClick={() => setTab('position')}>
            {t.tab_position}
            {boxesOf(field).length === 0 && <span className="fb-dot" />}
          </button>
        </div>
      }

      <div className="fb-inspector-body">
        {tab === 'position' && canPlace
          ? <PositionTab field={field} fields={fields} pages={pages} readOnly={readOnly} styleClipboard={styleClipboard} activeBox={activeBox} placing={placing} actions={actions} />
          : <FieldTab field={field} fields={fields} blueprint={blueprint} hasPdf={hasPdf} readOnly={readOnly} meta={meta} actions={actions} />
        }
      </div>
    </>
  )
}

const SectionInspector = ({ section, fields, hasPdf, readOnly, actions }) => {
  const t = useT()
  const [picker, setPicker] = useState(false)
  const children = fields.filter((field) => field.section_id === section.id)
  const hasTable = children.some((field) => field.data_type === 'table')
  const set = (changes) => actions.updateField(section.id, changes)

  return (
    <>
      <div className="fb-inspector-head">
        <TypeIcon type="section" />
        <div className="fb-inspector-title">
          <strong>{section.name || t.untitled}</strong>
          <span>{interpolate(t.section_count, { count: children.length })}</span>
        </div>
        {!readOnly &&
          <div className="fb-inspector-actions">
            <button type="button" className="fb-icon-button" title={t.duplicate} onClick={() => actions.duplicateField(section.id)}><i className="far fa-copy" /></button>
            <button type="button" className="fb-icon-button is-danger" title={t.delete} onClick={() => actions.deleteField(section.id)}><i className="far fa-trash-alt" /></button>
          </div>
        }
      </div>
      <div className="fb-inspector-body">
        <Group>
          <Row label={t.section_name}>
            <TextArea value={section.name} disabled={readOnly} onChange={(name) => set({ name })} />
          </Row>
          <Toggle
            checked={section.duplicable}
            disabled={readOnly || hasTable}
            label={t.duplicable}
            hint={hasTable ? t.table_not_in_loop : t.duplicable_hint}
            onChange={(duplicable) => set({ duplicable })}
          />
          {!hasPdf &&
            <Row label={t.text_align} inline>
              <Segmented value={section.text_align || 'left'} options={alignOptions(t)} disabled={readOnly} onChange={(text_align) => set({ text_align })} />
            </Row>
          }
        </Group>
        <ConditionEditor target={section} fields={fields} readOnly={readOnly} onChange={(visible_when) => set({ visible_when })} />
        {!readOnly &&
          <div className="fb-picker-anchor">
            <button type="button" className="fb-button is-block" onClick={() => setPicker(true)}>
              <i className="fas fa-plus" /> {t.add_field_here}
            </button>
            {picker &&
              <TypePicker
                exclude={section.duplicable ? ['table'] : []}
                onClose={() => setPicker(false)}
                onPick={(type) => { setPicker(false); actions.createField({ dataType: type, sectionId: section.id }) }}
              />
            }
          </div>
        }
      </div>
    </>
  )
}

const MultiInspector = ({ selectedFields, readOnly, actions }) => {
  const t = useT()
  const placed = selectedFields.filter((field) => boxesOf(field).length > 0)
  const boxes  = placed.flatMap(boxesOf)
  const first  = boxes[0] && { ...DEFAULT_POSITION, ...boxes[0].position }

  return (
    <>
      <div className="fb-inspector-head">
        <span className="fb-type-icon tone-slate"><i className="fas fa-object-group" /></span>
        <div className="fb-inspector-title">
          <strong>{interpolate(t.selected_count, { count: selectedFields.length })}</strong>
          <span>{interpolate(t.placed_count, { count: placed.length })}</span>
        </div>
      </div>
      <div className="fb-inspector-body">
        <div className="fb-prop-hint">{t.multi_hint}</div>
        {first && !readOnly &&
          <Group title={t.text_style}>
            <StyleControls
              style={first}
              onChange={(changes) => actions.updatePositions(Object.fromEntries(boxes.map((box) => [box.key, { ...box.position, ...changes }])))}
            />
          </Group>
        }
        {placed.length > 0 && !readOnly &&
          <button type="button" className="fb-button is-danger is-block" onClick={() => actions.removePositions(boxes.map((box) => box.key))}>
            <i className="fas fa-eraser" /> {t.remove_positions}
          </button>
        }
      </div>
    </>
  )
}

const BackgroundForm = ({ blueprint, meta }) => {
  const t = useT()
  const formRef = useRef(null)
  const fileRef = useRef(null)
  const [removing, setRemoving] = useState(false)

  // The background goes through the usual form post, which flattens and paginates it
  const submit = (remove) => {
    setRemoving(remove)
    setTimeout(() => formRef.current.submit(), 0)
  }

  return (
    <form ref={formRef} method="post" action={meta.urls.update} encType="multipart/form-data" className="fb-background">
      <input type="hidden" name="_method" value="patch" />
      <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''} />
      <input type="hidden" name="blueprint[name]" value={blueprint.name} />
      {!removing && <input type="hidden" name="pdf_background" value="true" />}
      <input ref={fileRef} type="file" name="blueprint[pdf_template]" accept="application/pdf" hidden onChange={() => submit(false)} />

      {blueprint.pdf_template
        ? (
          <div className="fb-file">
            <i className="far fa-file-pdf" />
            <a href={blueprint.pdf_template.url} target="_blank" rel="noopener noreferrer">{blueprint.pdf_template.filename}</a>
          </div>
        )
        : <div className="fb-prop-hint">{t.no_background_hint}</div>
      }
      <div className="fb-button-row">
        <button type="button" className="fb-button" onClick={() => fileRef.current.click()}>
          <i className={blueprint.pdf_template ? 'fas fa-sync-alt' : 'fas fa-plus'} /> {blueprint.pdf_template ? t.replace_pdf : t.add_pdf}
        </button>
        {blueprint.pdf_template &&
          <button
            type="button"
            className="fb-button is-danger"
            onClick={() => { if (window.confirm(t.remove_pdf_confirm)) { submit(true) } }}
          >
            <i className="far fa-trash-alt" /> {t.remove_pdf}
          </button>
        }
      </div>
      {blueprint.pdf_template && <div className="fb-prop-hint">{t.replace_pdf_hint}</div>}
    </form>
  )
}

const FormSettings = ({ blueprint, permissions, hasPdf, readOnly, meta, actions }) => {
  const t = useT()
  const set = actions.updateForm
  const surfaces = ['attachable_to_quote', 'attachable_to_workorder', 'attachable_to_purchase_order'].filter((key) => permissions[key])

  return (
    <>
      <div className="fb-inspector-head">
        <span className="fb-type-icon tone-slate"><i className="fas fa-cog" /></span>
        <div className="fb-inspector-title">
          <strong>{t.form_settings}</strong>
          <span>{blueprint.name}</span>
        </div>
      </div>
      <div className="fb-inspector-body">
        <Group>
          <Row label={t.form_name}>
            <input className="fb-input" value={blueprint.name || ''} maxLength={90} onChange={(event) => set({ name: event.target.value })} />
          </Row>
          {!hasPdf &&
            <Row label={t.display_name} hint={t.display_name_hint}>
              <input className="fb-input" value={blueprint.display_name || ''} onChange={(event) => set({ display_name: event.target.value })} />
            </Row>
          }
        </Group>

        {(surfaces.length > 1 || permissions.attachable_to_quote) &&
          <Group title={t.usage}>
            {surfaces.length > 1 && surfaces.map((key) => (
              <Toggle key={key} checked={blueprint[key]} label={t[key]} onChange={(value) => set({ [key]: value })} />
            ))}
            <Toggle checked={blueprint.can_be_filled} label={t.form_can_be_filled} hint={t.form_can_be_filled_hint} onChange={(value) => set({ can_be_filled: value })} />
          </Group>
        }

        {!hasPdf &&
          <Group title={t.layout}>
            <Row label={t.text_align} inline>
              <Segmented value={blueprint.text_align || 'left'} options={alignOptions(t)} onChange={(text_align) => set({ text_align })} />
            </Row>
            <Row label={t.label_width} hint={t.label_width_hint}>
              <NumberInput value={blueprint.label_width} min={0} suffix="mm" onChange={(label_width) => set({ label_width: label_width || 0 })} />
            </Row>
          </Group>
        }

        {permissions.edit_submit_url &&
          <Group title={t.submission}>
            <Row label={t.submit_url} hint={t.submit_url_hint}>
              <input className="fb-input" value={blueprint.submit_url || ''} placeholder="https://" onChange={(event) => set({ submit_url: event.target.value })} />
            </Row>
          </Group>
        }

        {!readOnly &&
          <Group title={t.background_pdf}>
            <BackgroundForm blueprint={blueprint} meta={meta} />
          </Group>
        }

        {hasPdf &&
          <Group title={t.shortcuts}>
            <dl className="fb-shortcuts">
              {t.shortcut_list.map(([keys, text]) => (
                <React.Fragment key={keys}><dt>{keys.split('+').map((key) => <kbd key={key}>{key}</kbd>)}</dt><dd>{text}</dd></React.Fragment>
              ))}
            </dl>
          </Group>
        }
      </div>
    </>
  )
}

const Inspector = ({ blueprint, fields, selectedFields, pages, permissions, readOnly, meta, styleClipboard, activeBox, placing, actions }) => {
  const hasPdf = pages.length > 0
  let content

  if (selectedFields.length > 1) {
    content = <MultiInspector selectedFields={selectedFields} readOnly={readOnly} actions={actions} />
  } else if (selectedFields.length === 1 && isSection(selectedFields[0])) {
    content = <SectionInspector key={selectedFields[0].id} section={selectedFields[0]} fields={fields} hasPdf={hasPdf} readOnly={readOnly} actions={actions} />
  } else if (selectedFields.length === 1) {
    content = (
      <FieldInspector
        key={selectedFields[0].id}
        field={selectedFields[0]}
        fields={fields}
        blueprint={blueprint}
        pages={pages}
        readOnly={readOnly}
        meta={meta}
        styleClipboard={styleClipboard}
        activeBox={activeBox}
        placing={placing}
        actions={actions}
      />
    )
  } else {
    content = <FormSettings blueprint={blueprint} permissions={permissions} hasPdf={hasPdf} readOnly={readOnly} meta={meta} actions={actions} />
  }

  return <aside className="fb-panel fb-inspector">{content}</aside>
}

export default Inspector
